import React, { useContext, useState } from 'react';
import { AppBar, Toolbar, Box, useTheme, useMediaQuery, Drawer } from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useFeedback } from './common/FeedbackProvider';
import { NAV_ITEMS, USER_MENU_ITEMS, visibleItems } from './navbar/navItems';
import NavDrawerContent from './navbar/NavDrawerContent';
import NavUserMenu from './navbar/NavUserMenu';
import DesktopToolbarContent from './navbar/DesktopToolbarContent';
import MobileToolbarContent from './navbar/MobileToolbarContent';

export default function NavBar() {
  const { user, role, logout, loading } = useContext(AuthContext);
  const { notify } = useFeedback();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (loading) return null;

  const navItems = visibleItems(NAV_ITEMS, hasPermission);
  const userMenuItems = visibleItems(USER_MENU_ITEMS, hasPermission);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      setAnchorEl(null);
      setMobileOpen(false);
      const { error } = await logout();

      if (error) {
        throw error;
      }

      // logout() ruimt lastSelectedProjectId en sessionStorage zelf op
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error.message);
      notify.error('Er ging iets mis bij het uitloggen. Probeer het opnieuw.');
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleClose();
    setMobileOpen(false);
    navigate(path);
  };

  const handleDrawerItemClick = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          backdropFilter: 'blur(10px)',
          borderBottom: 1,
          borderColor: 'onPrimary.hover',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          {isMobile ? (
            <MobileToolbarContent
              user={user}
              onDrawerToggle={handleDrawerToggle}
              onOpenMenu={handleMenu}
            />
          ) : (
            <DesktopToolbarContent user={user} items={navItems} onOpenMenu={handleMenu} />
          )}

          <NavUserMenu
            anchorEl={anchorEl}
            onClose={handleClose}
            user={user}
            role={role}
            items={userMenuItems}
            onItemClick={handleMenuItemClick}
            onLogout={handleLogout}
          />
        </Toolbar>
      </AppBar>

      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
              borderRadius: 0,
            },
          }}
        >
          <NavDrawerContent
            user={user}
            role={role}
            items={navItems}
            pathname={location.pathname}
            onClose={handleDrawerToggle}
            onNavigate={handleDrawerItemClick}
          />
        </Drawer>
      </Box>
    </>
  );
}
